import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Package,
  User,
  MapPin,
  Truck,
  Box,
  Calendar,
  ArrowLeft,
  FileText,
  Hash,
  Phone,
  Mail,
  Building2,
  Clock,
  CheckCircle,
  RotateCcw,
  Send,
} from "lucide-react";
import type { PackageStatus } from "@/types/logistics";

export const Route = createFileRoute("/_authenticated/logistics/packages/$id")({
  component: PackageDetailPage,
});

const STATUS_CONFIG: Record<
  PackageStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
    icon: typeof Package;
    color: string;
  }
> = {
  box_delivered: {
    label: "Caja Entregada",
    variant: "secondary",
    icon: Send,
    color: "bg-amber-100 text-amber-800 border-amber-200",
  },
  received: {
    label: "Recibido",
    variant: "default",
    icon: Clock,
    color: "bg-blue-100 text-blue-800 border-blue-200",
  },
  consolidated: {
    label: "Consolidado",
    variant: "secondary",
    icon: Package,
    color: "bg-purple-100 text-purple-800 border-purple-200",
  },
  in_transit: {
    label: "En Transito",
    variant: "outline",
    icon: Truck,
    color: "bg-cyan-100 text-cyan-800 border-cyan-200",
  },
  delivered: {
    label: "Entregado",
    variant: "default",
    icon: CheckCircle,
    color: "bg-green-100 text-green-800 border-green-200",
  },
  returned: {
    label: "Devuelto",
    variant: "destructive",
    icon: RotateCcw,
    color: "bg-red-100 text-red-800 border-red-200",
  },
};

function PackageDetailPage() {
  const { id } = Route.useParams();

  const packageData = useQuery(
    api.companies.logistics.packages.queries.getPackageById,
    { id: id as Id<"packages"> },
  );

  if (packageData === undefined) {
    return <LoadingSkeleton />;
  }

  if (packageData === null) {
    return <NotFoundState />;
  }

  const statusConfig = STATUS_CONFIG[packageData.status as PackageStatus] || {
    label: packageData.status,
    variant: "outline" as const,
    icon: Package,
    color: "bg-gray-100 text-gray-800",
  };

  const StatusIcon = statusConfig.icon;

  return (
    <div className="flex-1 flex flex-col min-h-0 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/logistics/packages">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="h-6 w-6" />
              Paquete #{packageData.orderNumber}
            </h1>
            {packageData.trackingNumber && (
              <p className="text-sm text-muted-foreground">
                Tracking: {packageData.trackingNumber}
              </p>
            )}
          </div>
        </div>
        <Badge className={statusConfig.color}>
          <StatusIcon className="h-4 w-4 mr-1" />
          {statusConfig.label}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Remitente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {packageData.sender ? (
              <>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{packageData.sender.name}</span>
                </div>
                {packageData.sender.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{packageData.sender.email}</span>
                  </div>
                )}
                {packageData.sender.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{packageData.sender.phone}</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted-foreground text-sm">
                Sin informacion del remitente
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Destinatario
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {packageData.recipient ? (
              <>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {packageData.recipient.name}
                  </span>
                </div>
                {packageData.recipient.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {packageData.recipient.email}
                    </span>
                  </div>
                )}
                {packageData.recipient.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {packageData.recipient.phone}
                    </span>
                  </div>
                )}
                {packageData.recipient.addresses &&
                  packageData.recipient.addresses.length > 0 && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <span className="text-sm">
                        {packageData.recipient.addresses[0].street}
                        {packageData.recipient.addresses[0].city &&
                          `, ${packageData.recipient.addresses[0].city}`}
                      </span>
                    </div>
                  )}
              </>
            ) : (
              <p className="text-muted-foreground text-sm">
                Sin informacion del destinatario
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Box className="h-5 w-5" />
              Detalles del Paquete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {packageData.boxType && (
              <div className="flex items-center gap-2">
                <Box className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  Tipo: {packageData.boxType.name}
                </span>
              </div>
            )}
            {packageData.realWeight && (
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  Peso: {packageData.realWeight} lbs
                </span>
              </div>
            )}
            {packageData.declaredValue && (
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  Valor Declarado: ${packageData.declaredValue}
                </span>
              </div>
            )}
            {packageData.carrier && (
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  Transportista: {packageData.carrier.name}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {packageData.merchandiseDescription && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Descripcion de Mercancia
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{packageData.merchandiseDescription}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Fechas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packageData.internalControl?.createdAt && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Creado
                </p>
                <p className="text-sm font-medium">
                  {new Date(
                    packageData.internalControl.createdAt,
                  ).toLocaleDateString("es-HN", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            )}
            {packageData.pickedUpAt && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Recogido
                </p>
                <p className="text-sm font-medium">
                  {new Date(packageData.pickedUpAt).toLocaleDateString(
                    "es-HN",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </p>
              </div>
            )}
            {packageData.deliveredAt && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Entregado
                </p>
                <p className="text-sm font-medium">
                  {new Date(packageData.deliveredAt).toLocaleDateString(
                    "es-HN",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {packageData.notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Notas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{packageData.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex-1 flex flex-col min-h-0 p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10 rounded-md" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-3">
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6">
      <Package className="h-16 w-16 text-muted-foreground mb-4" />
      <h2 className="text-xl font-semibold mb-2">Paquete no encontrado</h2>
      <p className="text-muted-foreground mb-4">
        El paquete que buscas no existe o no tienes acceso a el.
      </p>
      <Button asChild>
        <Link to="/logistics/packages">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a la lista
        </Link>
      </Button>
    </div>
  );
}
